export {
  isFetchBaseQueryError,
  rtkQueryErrorMessage,
} from './rtk-query-error'

interface FormatOptions {
  hideDecimal?: boolean
  hideSymbol?: boolean
  convertToNumber?: boolean
  symbol?: string
  removeLeadingZero?: boolean
  numberAbbreviate?: boolean
}

const formatNumberWithSymbols = (number: number) => {
  const formats = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'B' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'Q' },
    { value: 1e18, symbol: 'QT' },
    { value: 1e21, symbol: 'QQ' },
  ]
  const format = formats
    .slice()
    .reverse()
    .find((fmt) => number >= fmt.value)
  if (format) {
    return (
      (number / format.value).toFixed(2).replace(/\.00$/, '') + format.symbol
    )
  }
  return number.toString()
}

const getCurrencySymbol = (param: string) => {
  const nairaSymbol = '\u{020A6}'
  const upperParam = String(param).toUpperCase()
  return upperParam === 'NGN' || upperParam === 'NAIRA'
    ? nairaSymbol
    : upperParam === 'USD' || upperParam === 'DOLLAR'
      ? '$'
      : upperParam === 'EUR' || upperParam === 'EURO'
        ? '€'
        : upperParam === 'ZAR'
          ? 'R'
          : upperParam === 'KES'
            ? 'KSh'
            : upperParam === 'CAD'
              ? '$'
              : upperParam === 'GHS'
                ? '₵'
                : upperParam === 'GBP' ||
                    upperParam === 'POUNDS' ||
                    upperParam === 'POUND'
                  ? '£'
                  : param
}

export const numberFormat = (
  number: number | string | null | undefined,
  options?: FormatOptions
): string => {
  const symbol = getCurrencySymbol(options?.symbol || 'GBP')
  const hideDecimal = options?.hideDecimal || false
  const hideSymbol = options?.hideSymbol || false
  const convertToNumber = options?.convertToNumber || false
  const removeLeadingZero = options?.removeLeadingZero || false
  const numberAbbreviate = options?.numberAbbreviate || false
  if (number !== null && number !== undefined) {
    // Remove symbols and non-numeric characters
    const numericValue: string = number.toString().replace(/[^0-9.]/g, '')
    // Separate the integer and decimal parts
    let [integerPart, decimalPart] = numericValue.split('.')
    if (removeLeadingZero) {
      // Remove leading zeros if more than one leading zero
      if (/^0{2,}/.test(integerPart)) {
        integerPart = integerPart.replace(/^0+/, '')
      }
    }
    if (convertToNumber) {
      // Return number without thousand formatting and without symbol
      const val = decimalPart ? `${integerPart}.${decimalPart}` : integerPart
      return val
    }
    const formattedValue = Number(
      integerPart + (decimalPart ? '.' + decimalPart : '')
    )
    if (numberAbbreviate) {
      const formatted = formatNumberWithSymbols(formattedValue)
      return hideSymbol ? formatted : `${symbol}${formatted}`
    }
    // Format the integer part with thousand separator
    integerPart = Number(integerPart).toLocaleString()
    // Handle the decimal part
    if (!hideDecimal) {
      if (decimalPart) {
        // Convert the decimal part back to a number and round to two significant figures
        decimalPart = Number('0.' + decimalPart)
          .toPrecision(2)
          .split('.')[1]
        // Remove trailing zeros from the decimal part
        decimalPart = decimalPart?.replace(/0+$/, '') || ''
      }
      const val = decimalPart
        ? `${integerPart}${decimalPart ? '.' : ''}${decimalPart}`
        : `${integerPart}.00`
      return hideSymbol ? val : `${symbol}${val}`
    } else {
      return hideSymbol ? integerPart : `${symbol}${integerPart}`
    }
  } else {
    return hideSymbol ? `0.00` : `${symbol}0.00`
  }
}

/**
 * Generates a URL query string from a given object of parameters.
 *
 * Filters out parameters with `undefined` or `null` or empty string values. If a parameter value is an array,
 * it generates multiple key-value pairs for each element in the array.
 *
 * @param params - An object containing key-value pairs to be converted into a query string. Values can be strings, numbers, or arrays of these types.
 * @returns The generated query string, starting with '?' if there are parameters, or an empty string if no valid parameters are provided.
 *
 * @example
 * generateQueryString({ foo: "bar", baz: [1, 2], empty: undefined });
 * // Returns: "?foo=bar&baz=1&baz=2"
 */
export function generateQueryString(params: Record<string, any>): string {
  const query = Object.entries(params)
    .filter(
      ([_, value]) => value !== undefined && value !== null && value !== ''
    ) // filter out undefined/null
    .map(([key, value]) =>
      Array.isArray(value)
        ? value
            .map(
              (val) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`
            )
            .join('&')
        : `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join('&')

  return query ? `?${query}` : ''
}

export const removeKeyFromObject = (obj: any, key: string) => {
  const { [key]: _, ...rest } = obj
  return rest
}

/**
 * Formats the current date and time for display
 * @returns Object containing formatted date and time strings
 */
export const getCurrentDateTime = () => {
  const now = new Date()

  // Format date as "Monday, September 01"
  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'long',
    day: '2-digit',
  }
  const formattedDate = now.toLocaleDateString('en-US', dateOptions)

  // Get time of day for greeting
  const hour = now.getHours()
  let timeOfDay = 'Good Morning!'
  if (hour >= 12 && hour < 17) {
    timeOfDay = 'Good Afternoon!'
  } else if (hour >= 17) {
    timeOfDay = 'Good Evening!'
  }

  return {
    formattedDate,
    timeOfDay,
    currentTime: now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }),
  }
}

/**
 * Returns the appropriate CSS class for trend type styling
 * @param changeType - The change type ('positive' | 'negative' | undefined)
 * @returns CSS class string for the trend color
 */
export const getTrendTypeClass = (
  changeType?: 'positive' | 'negative' | string
): string => {
  if (changeType === 'positive') {
    return 'text-green-600'
  }
  if (changeType === 'negative') {
    return 'text-red-500'
  }
  return 'text-[#A3A3A3]'
}

/**
 * Formats a numeric string with comma separators for thousands
 * @param value - The numeric string to format (e.g., "1000" or "10000.50")
 * @returns Formatted string with commas (e.g., "1,000" or "10,000.50")
 * @example
 * formatAmountWithCommas("1000") // Returns: "1,000"
 * formatAmountWithCommas("10000.50") // Returns: "10,000.50"
 */
export const formatAmountWithCommas = (value: string): string => {
  // Remove all non-digit characters except decimal point
  const numericValue = value.replace(/[^\d.]/g, '')

  // Split by decimal point
  const parts = numericValue.split('.')
  const integerPart = parts[0]
  const decimalPart = parts[1]

  // Add commas to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  // Combine with decimal part if exists
  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger
}

/**
 * Removes comma separators from a formatted numeric string
 * @param value - The formatted string with commas (e.g., "1,000" or "10,000.50")
 * @returns Clean numeric string without commas (e.g., "1000" or "10000.50")
 * @example
 * removeCommas("1,000") // Returns: "1000"
 * removeCommas("10,000.50") // Returns: "10000.50"
 */
export const removeCommas = (value: string): string => {
  return value.replace(/,/g, '')
}

/**
 * Handles amount input change with formatting and validation
 * Formats the input value with commas and limits decimal places to 2
 * @param value - The input value from the event
 * @returns Object containing formatted display value and clean numeric value
 * @example
 * const result = handleAmountInputChange("10000.50")
 * // Returns: { displayValue: "10,000.50", numericValue: "10000.50" }
 */
export const handleAmountInputChange = (
  value: string
): { displayValue: string; numericValue: string } | null => {
  // Remove commas first to work with clean numeric value
  let cleanValue = removeCommas(value)

  // Allow empty, numbers, and one decimal point
  if (cleanValue === '' || /^\d*\.?\d*$/.test(cleanValue)) {
    // Limit decimal places to 2
    const parts = cleanValue.split('.')
    if (parts[1] && parts[1].length > 2) {
      cleanValue = `${parts[0]}.${parts[1].substring(0, 2)}`
    }

    const formattedValue = formatAmountWithCommas(cleanValue)
    return {
      displayValue: formattedValue,
      numericValue: cleanValue,
    }
  }

  return null
}

/**
 * Formats a date string into a formatted date and time object
 * Returns date in "DD-MM-YYYY" format and time in "HH:MMAM/PM" format (12-hour)
 * @param dateString - ISO date string or any valid date string
 * @returns Object containing formatted date and time strings
 * @example
 * formatDateTime("2025-01-09T17:45:00.000000Z")
 * // Returns: { date: "09-01-2025", time: "05:45PM" }
 */
export const formatDateTime = (
  dateString: string
): {
  date: string
  time: string
} => {
  const date = new Date(dateString)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const formattedDate = `${day}-${month}-${year}`

  const hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  // For 17:45, display as 05:45PM (12-hour format)
  const displayHours = hours % 12 || 12
  const formattedTime = `${String(displayHours).padStart(2, '0')}:${minutes}${ampm}`

  return { date: formattedDate, time: formattedTime }
}

/**
 * Formats a time string (HH:MM format) to 12-hour format with am/pm
 * @param time - Time string in "HH:MM" format (e.g., "08:00", "17:45")
 * @returns Formatted time string in "H:MM am/pm" format (e.g., "8:00 am", "5:45 pm")
 * @example
 * formatTime("08:00") // Returns: "8:00 am"
 * formatTime("17:45") // Returns: "5:45 pm"
 * formatTime("12:00") // Returns: "12:00 pm"
 */
export const formatTime = (time: string): string => {
  if (!time) return '----'
  try {
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours, 10)
    if (isNaN(hour)) return time
    const ampm = hour >= 12 ? 'pm' : 'am'
    const displayHour = hour % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  } catch {
    return time
  }
}
